<template>
  <div v-if="!isLoadingInit" class="resolution-block">
    <b-row v-if="resolution.created_during_meeting" class="my-4">
      <!-- Label -->
      <b-col cols="12" sm="8">
        <label
          v-if="
            $_.isEmpty(parentResolutionOnCreateDuringMeeting) && meetingParentResolution == null
          "
          class="mr-5"
          >{{ $t("resolutions.no") }}</label
        >
        <label v-else class="mr-5">{{ $t("resolutions.sub_no") }}</label>
        <!-- Display parent resolution number and then the children resolution number if is a children resolution -->
        <span v-if="meetingParentResolution" class="mr-1">{{
          meetingParentResolution.resolution_number + "."
        }}</span>
        <span v-else class="mr-1">{{ meetingResolutionNumberFixed }}</span>
        <ValidationProvider
          ref="vee-resolution-number"
          v-slot="{ errors, classes }"
          :name="'n°'"
          rules="required|alpha"
          class="d-inline-flex"
        >
          <b-input
            :value="meetingResolutionNumberDynamic"
            class="input-resolution-number"
            :maxlength="1"
            @input="changeResolutionNumber($event)"
          />
          <span :class="classes">{{ errors[0] }}</span>
        </ValidationProvider>
      </b-col>
    </b-row>
    <b-card class="mb-4" :class="allChildrenResolutions.length > 0 ? 'bg-light' : ''">
      <b-row class="justify-content-between my-4">
        <!-- Label -->
        <b-col cols="12" sm="8" class="mb-4">
          <label
            v-if="$_.isEmpty(parentResolutionOnCreateDuringMeeting) && !isInMeetingStartAsChildren"
            >{{ $t("resolutions.libelle_resolution") }}</label
          >
          <label v-else class="mr-5">{{ $t("resolutions.libelle_sous_resolution") }}</label>
          <ValidationProvider
            v-if="isInMeeting"
            ref="vee-resolution-name"
            v-slot="{ errors, classes }"
            :name="'nom'"
            rules="required"
          >
            <b-input v-model="resolution.name" />
            <span :class="classes">{{ errors[0] }}</span>
          </ValidationProvider>
          <div v-else class="custom-select-autocomplete w-100">
            <ValidationProvider
              ref="vee-resolution-name"
              v-slot="{ errors, classes }"
              :name="'nom'"
              rules="required"
            >
              <b-input
                ref="v-input"
                class="custom-select-autocomplete-input"
                autocomplete="off"
                :value="resolution.name"
                @input="input"
                @focus="focus"
                @blur="blur"
              />
              <span :class="classes">{{ errors[0] }}</span>
            </ValidationProvider>
            <v-select
              ref="v-select"
              class="custom-select-autocomplete-select"
              :options="resolutions"
              label="name"
              @input="select($event)"
            >
              <span slot="no-options">
                {{ $t("general.select_with_input_no_option") }}
              </span>
            </v-select>
          </div>
        </b-col>
        <!-- Majority -->
        <b-col
          v-if="allChildrenResolutions.length == 0 && majoritiesOptions().length > 0"
          cols="10"
          sm="4"
        >
          <label>{{ $t("resolutions.majority") }}</label>
          <ValidationProvider
            ref="vee-resolution-majority_id"
            v-slot="{ errors, classes }"
            name="majorité"
            rules="required"
          >
            <v-select
              v-if="resolution"
              v-model="resolution.majority_id"
              class="w-auto"
              :reduce="name => name.id"
              :options="majoritiesOptions()"
              label="name"
            />
            <span :class="classes">{{ errors[0] }}</span>
          </ValidationProvider>
          <span v-if="resolution">
            <div v-if="resolution.revote_majority_id">
              <label class="text-info">Majorité revote&nbsp;:</label>
              <b-card>
                {{
                  $_.find(majoritiesOptions(), function (majority) {
                    return majority.id == resolution.revote_majority_id;
                  }).name
                }}
              </b-card>
            </div>
          </span>
        </b-col>
        <!-- Details -->
        <b-col cols="12" class="mb-4">
          <label
            v-if="$_.isEmpty(parentResolutionOnCreateDuringMeeting) && !isInMeetingStartAsChildren"
            >{{ $t("resolutions.details_resolution") }}</label
          >
          <label v-else>{{ $t("resolutions.details_sous_resolution") }}</label>
          <tiptap
            v-if="resolution"
            v-model="resolution.description"
            @updateOnFocusOut="updateResolutionDescription"
          />
        </b-col>
        <!-- Voting Category -->
        <b-col
          v-if="
            isInMeeting &&
            allChildrenResolutions.length == 0 &&
            resolution.majority_id != defaultSansVoteMajorityId
          "
          cols="10"
        >
          <label>Clés</label>
          <ValidationProvider
            v-if="!isInMeetingUpsert"
            ref="vee-resolution-voting_category_id"
            v-slot="{ errors, classes }"
            name="clés"
            rules="required"
          >
            <v-select
              v-model="resolution.voting_category_id"
              class="w-100"
              :reduce="name => name.id"
              :options="votingCategories"
              label="name"
            >
              <span slot="no-options">
                {{ $t("general.select_no_option") }}
              </span>
            </v-select>
            <span :class="classes">{{ errors[0] }}</span>
          </ValidationProvider>
          <v-select
            v-else
            v-model="resolution.voting_category_id"
            class="w-100"
            :reduce="name => name.id"
            :options="votingCategories"
            label="name"
          >
            <span slot="no-options">
              {{ $t("general.select_no_option") }}
            </span>
          </v-select>
        </b-col>
        <!-- NOTE: Media -->
        <b-col
          v-if="
            $_.isEmpty(priorPositionedResolutionOnCreateDuringMeeting) &&
            $_.isEmpty(parentResolutionOnCreateDuringMeeting)
          "
          cols="12"
          class="upload-pdf-resolution my-5"
        >
          <resolutionMedia
            :resolution="resolution"
            :resolution-id="resolutionId"
            :meeting-resolution-id="meetingResolutionId"
            :is-in-meeting-start="isInMeetingStart"
            :is-in-meeting="isInMeeting"
            @updateFileRecords="updateFileRecords"
            @deleteFileRecord="deleteFileRecord"
          ></resolutionMedia>
        </b-col>
      </b-row>
    </b-card>
    <!-- REVIEW: Open this in v2 Resolution's Children -->
    <b-row v-if="!isInMeetingStart && !isChildResolution && !isCreatedInMeetingStart">
      <b-col :key="keySubResolution">
        <h4 class="mb-4">
          Sous résolutions
          <b-button class="button btn-blue px-4" @click="addChildResolution">
            <font-awesome-icon icon="plus"></font-awesome-icon>
          </b-button>
        </h4>
        <!-- NOTE: concatenated array of new children resolutions with existing children resolutions -->
        <b-card
          v-for="(childResolution, index) in allChildrenResolutions"
          :key="`child-resolution-${index}`"
          class="my-3"
        >
          <b-row class="mb-3">
            <b-col v-if="childResolution.position" cols="12" sm="2">
              <label for="position">Position</label>
              <ValidationProvider
                :ref="`vee-child-resolution-position-${index}`"
                v-slot="{ errors, classes }"
                :name="'position'"
                :rules="'required|min_value:1|max_value:' + maxPositionInput"
              >
                <b-form-input
                  id="position"
                  v-model="childResolution.position"
                  type="number"
                  :min="1"
                  :max="maxPositionInput"
                  @change="updateChildResolutionPosition($event, childResolution, index)"
                ></b-form-input>
                <span :class="classes">{{ errors[0] }}</span>
              </ValidationProvider>
            </b-col>
            <b-col cols="12" sm="10">
              <label>{{ $t("resolutions.libelle_sous_resolution") }}</label>
              <ValidationProvider
                :ref="`vee-child-resolution-name-${index}`"
                v-slot="{ errors, classes }"
                :name="'nom'"
                rules="required"
              >
                <b-input
                  v-model="childResolution.name"
                  @change="updateChildResolution(childResolution, index)"
                />
                <span :class="classes">{{ errors[0] }}</span>
              </ValidationProvider>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <b-col v-if="isInMeeting && childResolution.majority_id != defaultSansVoteMajorityId">
              <label>Clés</label>
              <ValidationProvider
                v-if="!isInMeetingUpsert"
                :ref="`vee-child-resolution-voting_category-${index}`"
                v-slot="{ errors, classes }"
                name="clés"
                rules="required"
              >
                <v-select
                  v-model="childResolution.voting_category_id"
                  class="w-100 bg-white"
                  :reduce="name => name.id"
                  :options="votingCategories"
                  label="name"
                >
                  <span slot="no-options">
                    {{ $t("general.select_no_option") }}
                  </span>
                </v-select>
                <span :class="classes">{{ errors[0] }}</span>
              </ValidationProvider>
              <v-select
                v-else
                v-model="childResolution.voting_category_id"
                class="w-100 bg-white"
                :reduce="name => name.id"
                :options="votingCategories"
                label="name"
              >
                <span slot="no-options">
                  {{ $t("general.select_no_option") }}
                </span>
              </v-select>
            </b-col>
            <b-col v-if="majoritiesOptions().length > 0" cols="10" sm="4">
              <label>{{ $t("resolutions.majority") }}</label>
              <ValidationProvider
                :ref="`vee-child-resolution-majority-${index}`"
                v-slot="{ errors, classes }"
                name="majorité"
                rules="required"
              >
                <v-select
                  v-if="childResolution"
                  v-model="childResolution.majority_id"
                  class="w-auto bg-white"
                  :reduce="name => name.id"
                  :options="majoritiesOptions()"
                  label="name"
                  @input="updateChildResolution(childResolution, index)"
                />
                <span :class="classes">{{ errors[0] }}</span>
              </ValidationProvider>
              <span v-if="childResolution">
                <div v-if="childResolution.revote_majority_id">
                  <label class="text-info">Majorité revote&nbsp;:</label>
                  <b-card>
                    {{
                      $_.find(majoritiesOptions(), function (majority) {
                        return majority.id == childResolution.revote_majority_id;
                      }).name
                    }}
                  </b-card>
                </div>
              </span>
            </b-col>
          </b-row>
          <b-row class="mb-3">
            <!-- Details -->
            <b-col>
              <label>{{ $t("resolutions.details_sous_resolution") }}</label>
              <tiptap
                v-model="childResolution.description"
                @updateOnFocusOut="updateChildResolutionDescription($event, childResolution, index)"
              />
            </b-col>
          </b-row>
          <b-row class="my-5">
            <b-col cols="12" class="upload-pdf-resolution">
              <resolutionMedia
                :resolution="childResolution"
                :resolution-id="childResolution.id"
                :meeting-resolution-id="childResolution.id"
                :is-in-meeting-start="isInMeetingStart"
                :is-in-meeting="isInMeeting"
                :key-id="childResolution.keyId"
                @updateFileRecords="updateFileRecords"
                @deleteFileRecord="deleteFileRecord"
              ></resolutionMedia>
            </b-col>
          </b-row>
          <!-- NOTE: Media -->
          <div class="d-flex justify-content-end">
            <b-button
              v-if="!childResolution.position"
              class="button btn-green px-5 mr-3"
              title="Confirm la résolution"
              @click="confirmChildResolution(childResolution)"
            >
              <font-awesome-icon icon="check"></font-awesome-icon>
            </b-button>
            <b-button
              v-if="childResolution.keyId"
              class="button btn-red px-5"
              title="Supprimer la résolution"
              @click="removeChildResolution(index)"
            >
              <font-awesome-icon icon="trash-alt"></font-awesome-icon>
            </b-button>
            <b-button
              v-else
              title="Supprimer la résolution"
              class="button btn-red px-5"
              @click="DELETE_CHILD_RESOLUTION(childResolution)"
            >
              <font-awesome-icon icon="trash-alt"></font-awesome-icon>
            </b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <!-- cancel, create, update -->
    <b-row
      class="mt-5 mb-2 justify-content-end"
      :class="{ 'justify-content-center': isInMeetingStart }"
    >
      <b-col v-if="!isInMeetingStart" cols="auto">
        <b-button class="button btn-red" @click="cancel(false)">
          <font-awesome-icon class="agv-button-icon" icon="times-circle" />
          <span>{{ $t("general.cancel") }}</span>
        </b-button>
      </b-col>
      <b-col cols="auto">
        <b-button
          v-if="isNew"
          :disabled="isLoadingSave"
          class="button btn-blue"
          @click="CREATE_RESOLUTION"
        >
          <font-awesome-icon v-if="!isLoadingSave" class="agv-button-icon" icon="plus" />
          <font-awesome-icon v-else class="agv-button-icon" icon="spinner" pulse />
          <span>{{ $t("general.create") }}</span>
        </b-button>
        <b-button
          v-if="!isNew || isInMeetingStart"
          class="button btn-blue"
          :disabled="isLoadingSave"
          @click="UPDATE_RESOLUTION"
        >
          <font-awesome-icon v-if="!isLoadingSave" class="agv-button-icon" icon="save" />
          <font-awesome-icon v-else class="agv-button-icon" icon="spinner" pulse />
          <span>{{ $t("general.enregistrer") }}</span>
        </b-button>
      </b-col>
    </b-row>
  </div>
  <appLoader v-else />
</template>

<script>
import { validate } from "vee-validate";

import ResolutionORM from "@/store/orm-axios/models/Resolution";
import ResolutionVoteORM from "@/store/orm-axios/models/ResolutionVote";
import MeetingResolutionORM from "@/store/orm-axios/models/MeetingResolution";
import MajorityORM from "@/store/orm-axios/models/Majority";
import VotingCategoryORM from "@/store/orm-axios/models/VotingCategory";
import MeetingCorrespondenceVoteORM from "@/store/orm-axios/models/MeetingCorrespondenceVote";
import BuildingMajoritiesORM from "@/store/orm-axios/models/BuildingMajorities";

import ResolutionMW from "@/middlewares/Resolution";
import MeetingResolutionMW from "@/middlewares/MeetingResolution";
import MediaMW from "@/middlewares/Media";
import MeetingCorrespondenceVoteMW from "@/middlewares/MeetingCorrespondenceVote";

import resolutionMedia from "./ResolutionMedia.vue";

var uniqid = require("uniqid");

import { defaultSansVoteMajorityId } from "@/constants/default.js";
import Tiptap from "@/components/snippets/Tiptap.vue";

import MeetingORM from "@/store/orm-axios/models/Meeting";

export default {
  components: {
    resolutionMedia,
    Tiptap,
  },
  props: {
    isNew: {
      type: Boolean,
      default: true,
    },

    resolutionId: {
      type: String,
      default: "",
    },
    resolutionCatalogId: {
      type: String,
      default: "",
    },
    meetingResolutionId: {
      type: String,
      default: "",
    },
    meetingParentResolutionId: {
      type: String,
      default: "",
    },
    meetingId: {
      type: String,
      default: "",
    },
    buildingId: {
      type: String,
      default: "",
    },

    lastPosition: {
      type: Number,
      default: 0,
    },

    meetingIsCompleted: {
      type: Boolean,
      default: false,
    },
    meetingResolutionStatus: {
      type: Number,
      default: null,
    },
    priorPositionedResolutionOnCreateDuringMeeting: {
      type: Object,
      default: () => {
        return {};
      },
    },
    parentResolutionOnCreateDuringMeeting: {
      type: Object,
      default: () => {
        return {};
      },
    },

    isCreatedInMeetingStart: {
      type: Boolean,
      default: false,
    },

    isInMeeting: {
      type: Boolean,
      default: false,
    },
    isInMeetingStart: {
      type: Boolean,
      default: false,
    },
    isInMeetingStartAsChildren: {
      type: Boolean,
      default: false,
    },
    meetingResolutionMajorityId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      defaultSansVoteMajorityId,
      isLoadingInit: true,
      isLoadingSave: false,
      resolution: {},
      // NOTE: Files
      // files: [],
      childrenResolutionsNew: [],
      childrenResolutionsConcatenated: [],
      fileRecords: [],
      allChildrenResolutions: [],
      keySubResolution: 1,
      meetingParentResolution: null,
    };
  },
  computed: {
    maxPositionInput() {
      return this.allChildrenResolutions.length;
    },
    votingCategories() {
      if (!this.isInMeetingUpsert) {
        return this.meetingDetails.VotingCategoriesData;
      }
      return this.filterVotingCategories();
    },
    resolutions() {
      return ResolutionORM.query()
        .where("agency_id", this.agencyId)
        .orderBy("created", "desc")
        .get();
    },
    isChildResolution() {
      return this.resolution ? !!this.resolution.parent_resolution_id : false;
    },
    isParentResolution() {
      try {
        return this.resolution.children_resolutions.length > 0;
      } catch {
        return false;
      }
    },
    meetingResolutionNumberFixed() {
      if (!this.$_.isEmpty(this.parentResolutionOnCreateDuringMeeting)) {
        const split = this.parentResolutionOnCreateDuringMeeting.resolution_number.split(".");
        return split[0] + ".";
      } else {
        const split = this.resolution.resolution_number.split(".");
        return split[0] + ".";
      }
    },
    meetingResolutionNumberDynamic() {
      if (!this.isChildResolution) {
        const split = this.resolution.resolution_number.split(".");
        return split[1];
      } else {
        return this.resolution.resolution_number;
      }
    },
    childrenResolutions() {
      return ResolutionORM.query()
        .where("agency_id", this.agencyId)
        .where("parent_resolution_id", this.resolutionId)
        .with("media")
        .orderBy("position", "asc")
        .get();
    },
    childrenMeetingResolutions() {
      return MeetingResolutionORM.query()
        .where("agency_id", this.agencyId)
        .where("parent_resolution_id", this.meetingResolutionId)
        .with("media")
        .orderBy("position", "asc")
        .get();
    },
    meetingCorrespondenceVotes() {
      return MeetingCorrespondenceVoteORM.query().where("meeting_id", this.meetingId).get();
    },
    buildingMajorities() {
      return BuildingMajoritiesORM.query()
        .where("agency_id", this.agencyId)
        .where("building_id", this.buildingId)
        .get();
    },
  },
  watch: {
    meetingResolutionStatus(newVal, oldVal) {
      if (this.meetingIsCompleted) {
        this.resolution.status = newVal;
      }
    },
  },
  async created() {
    // console.log("this.meetingResolutionId", this.meetingResolutionId);
    // var mr = MeetingResolutionORM.find(this.meetingResolutionId);
    // if (mr && mr.parent_resolution_id == null) {
    //   console.log("delete resolution votes");
    //   ResolutionVoteORM.delete(rv => {
    //     return rv.meeting_has_resolution_id != this.meetingResolutionId;
    //   });
    // }

    if (!this.isInMeeting) {
      await ResolutionMW.FETCH(this.agencyId);
    } else {
      if (this.meetingResolutionId) {
        await MeetingResolutionMW.GET(this.meetingResolutionId);
      }
    }

    if (!this.isNew) {
      if (this.isInMeeting) {
        this.isLoadingInit = false;
        this.resolution = MeetingResolutionORM.query()
          .where("id", this.meetingResolutionId)
          .with("media")
          .with("children_resolutions", query => {
            // query.orderBy("position");
            query.with("media");
          })
          .orderBy("position")
          .first();

        this.allChildrenResolutions = this.childrenMeetingResolutions;
        this.meetingParentResolution = MeetingResolutionORM.query()
          .where("id", this.meetingParentResolutionId)
          .first();
      } else {
        await ResolutionMW.GET(this.resolutionId)
          .then(res => {
            this.isLoadingInit = false;
            this.resolution = ResolutionORM.query()
              .where("id", this.resolutionId)
              .with("media")
              .with("children_resolutions", query => {
                // query.orderBy("position");
                query.with("media");
              })
              .first();
            this.allChildrenResolutions = this.childrenResolutions;
          })
          .catch(err => {
            this.isLoadingInit = false;
            console.log("err", err);
          });
      }
    } else {
      if (this.isInMeeting) {
        var position = -1;
        var resolutionNumber = "";

        if (!this.$_.isEmpty(this.priorPositionedResolutionOnCreateDuringMeeting)) {
          position = this.priorPositionedResolutionOnCreateDuringMeeting.position + 1;
          resolutionNumber = this.priorPositionedResolutionOnCreateDuringMeeting.resolution_number.split(
            "."
          )[0];
        }

        if (!this.$_.isEmpty(this.parentResolutionOnCreateDuringMeeting)) {
          position =
            MeetingResolutionORM.query()
              .where(mt => {
                return mt.parent_resolution_id == this.parentResolutionOnCreateDuringMeeting.id;
              })
              .get().length + 1;
          resolutionNumber = "";
        }

        this.resolution = {
          agency_id: this.agencyId,
          name: "",
          description: "",
          position: position,
          resolution_number: resolutionNumber,
          created_during_meeting:
            !this.$_.isEmpty(this.priorPositionedResolutionOnCreateDuringMeeting) ||
            !this.$_.isEmpty(this.parentResolutionOnCreateDuringMeeting)
              ? 1
              : 0,
          parent_resolution_id: !this.$_.isEmpty(this.parentResolutionOnCreateDuringMeeting)
            ? this.parentResolutionOnCreateDuringMeeting.id
            : null,

          resolution_catalog_id: null, // diff
          majority_id: null,

          // diff
          meeting_id: this.meetingId,
          voting_category_id: null,
          status: null,
        };
      } else {
        this.resolution = {
          agency_id: this.agencyId,
          name: "",
          description: "",
          position: -1,

          resolution_catalog_id: this.resolutionCatalogId, // diff
          majority_id: null,
        };
      }
      this.isLoadingInit = false;
    }
  },
  methods: {
    downloadItem(filePath) {
      var self = this;
      var fileUrl;
      if (!process.env.VUE_APP_IS_WEB) {
        // DESKTOP
        var fs = require("fs");
        fs.readFile(filePath, function (err, data) {
          if (err) {
            console.error(err);
            throw err;
          }
          fileUrl = window.URL.createObjectURL(new Blob([data]));
          var fileLink = document.createElement("a");
          let filename = filePath.substring(filePath.lastIndexOf("/") + 1);
          fileLink.href = fileUrl;
          fileLink.setAttribute("download", filename);
          document.body.appendChild(fileLink);
          fileLink.click();
        });
      } else {
        // WEB
        window.open(filePath, "_blank");
      }
    },
    async validateInputs() {
      var valid = true;
      await validate(this.resolution.name, "required", {}).then(res => {
        if (!res.valid) {
          valid = false;
          this.$refs["vee-resolution-name"].validate();
        }
      });
      if (this.majoritiesOptions.length > 0) {
        await validate(this.resolution.majority_id, "required", {}).then(res => {
          if (!res.valid) {
            valid = false;
            this.$refs["vee-resolution-majority_id"].validate();
          }
        });
      }

      if (this.resolution.created_during_meeting) {
        await validate(this.meetingResolutionNumberDynamic, "required|alpha", {}).then(res => {
          if (!res.valid) {
            valid = false;
            this.$refs["vee-resolution-number"].validate();
          }
        });
      }

      // NOTE: if resolution has children resolutions, voting_category_id is removed and we don't need to validate it
      if (
        this.isInMeeting &&
        !this.isInMeetingUpsert &&
        this.resolution.majority_id != this.defaultSansVoteMajorityId &&
        this.allChildrenResolutions.length == 0
      ) {
        await validate(this.resolution.voting_category_id, "required", {}).then(res => {
          if (!res.valid) {
            valid = false;
            this.$refs["vee-resolution-voting_category_id"].validate();
          }
        });
      }

      // NOTE: no need to validate children if in meeting start, they are updated separately
      if (!this.isInMeetingStart) {
        for (let i = 0; i < this.allChildrenResolutions.length; i++) {
          await validate(this.allChildrenResolutions[i].name, "required", {}).then(res => {
            if (!res.valid) {
              valid = false;
              this.$refs[`vee-child-resolution-name-${i}`][0].validate();
            }
          });
          if (this.majoritiesOptions.length > 0) {
            await validate(this.allChildrenResolutions[i].majority_id, "required", {}).then(res => {
              if (!res.valid) {
                valid = false;
                this.$refs[`vee-child-resolution-majority-${i}`][0].validate();
              }
            });
          }
          await validate(this.allChildrenResolutions[i].position, "required", {}).then(res => {
            if (!res.valid) {
              valid = false;
              this.$refs[`vee-child-resolution-position-${i}`][0].validate();
            }
          });
          await this.$refs[`vee-child-resolution-position-${i}`][0].validate().then(res => {
            if (!res.valid) {
              valid = false;
              this.$refs[`vee-child-resolution-name-${i}`][0].validate();
            }
          });
          if (
            this.isInMeeting &&
            !this.isInMeetingUpsert &&
            this.allChildrenResolutions[i].majority_id != this.defaultSansVoteMajorityId
          ) {
            await this.$refs[`vee-child-resolution-voting_category-${i}`][0]
              .validate()
              .then(res => {
                if (!res.valid) {
                  valid = false;
                  this.$refs[`vee-child-resolution-voting_category-${i}`][0].validate();
                }
              });
          }
        }
      }

      return valid;
    },
    // input + select + autocomplete functions
    input(e) {
      this.resolution.name = e;
      this.$refs["v-select"].search = e;
    },
    focus(e) {
      this.$refs["v-select"].open = true;
    },
    blur(e) {
      this.$refs["v-select"].open = false;
    },
    select(e) {
      this.REPLICATE_RESOLUTION(e);
    },
    cancel(preventDelete) {
      if (!preventDelete && this.fileRecords.length > 0) {
        this.fileRecords.forEach((file, index) => {
          if (file.media.isNew) {
            this.removeFile(index, file);
          }
        });
      }
      this.$emit("cancel");
    },
    async CREATE_RESOLUTION() {
      this.removeChildResolutionNotConfirmed();

      if ((await this.validateInputs()) == false) {
        return;
      }

      // !!!: create resolution during meeting
      if (this.resolution.created_during_meeting) {
        this.$emit("createResolutionDuringMeeting", this.resolution);
        return;
      }

      this.isLoadingSave = true;

      // Update new resolution to latest position
      this.resolution.position = this.lastPosition + 1;

      // Get only media from fileRecords
      let newMediaFiles = [];
      this.fileRecords.forEach((fileRecord, index) => {
        // HACK Set position for the media object which is not sync with the database, because is needed on resolution_for_reset
        if (!fileRecord.media.position) {
          fileRecord.media.position = index + 1;
        }
        newMediaFiles.push(fileRecord.media);
      });

      // NOTE: If Resolution has children resolutions, remove majority and votiong_category
      if (this.allChildrenResolutions.length > 0) {
        this.resolution.majority_id = null;
        this.resolution.voting_category_id = null;
      }

      if (this.isInMeeting) {
        await MeetingResolutionMW.CREATE_MEETING_RESOLUTION_WITH_MEDIA(
          this.resolution,
          this.allChildrenResolutions,
          newMediaFiles
        )
          .then(() => {
            this.$emit("reloadTableData");
            this.isLoadingSave = false;

            this.cancel(true);
          })
          .catch(err => {
            this.isLoadingSave = false;
            console.log("err", err);
          });
      } else {
        await ResolutionMW.CREATE_RESOLUTION_WITH_MEDIA(
          this.resolution,
          this.allChildrenResolutions,
          newMediaFiles
        )
          .then(() => {
            this.$emit("reloadTableData");
            this.isLoadingSave = false;
            this.cancel(true);
          })
          .catch(err => {
            this.isLoadingSave = false;
            // console.log("err", err);
          });
      }
    },
    async UPDATE_RESOLUTION() {
      this.removeChildResolutionNotConfirmed();

      if ((await this.validateInputs()) == false) {
        return;
      }

      this.isLoadingSave = true;

      // Get only media from fileRecords
      let newMediaFiles = [];
      this.fileRecords.forEach((fileRecord, index) => {
        // HACK Set position for the media object which is not sync with the database, because is needed on resolution_for_reset
        if (!fileRecord.media.position) {
          fileRecord.media.position = index + 1;
        }
        newMediaFiles.push(fileRecord.media);
      });

      // NOTE: If Resolution has children resolutions, remove majority and votiong_category
      if (this.allChildrenResolutions.length > 0) {
        this.resolution.majority_id = null;
        this.resolution.voting_category_id = null;
      }

      if (this.isInMeeting) {
        // NOTE: set meeting resolution status and revote_majority_id to null & remove resolution events from meeting minutes
        this.resolution.status = null;
        this.resolution.revote_majority_id = null;
        if (
          this.resolution.children_resolutions &&
          this.resolution.children_resolutions.length > 0
        ) {
          this.resolution.children_resolutions.forEach(childResolution => {
            childResolution.status = null;
            childResolution.revote_majority_id = null; // ???: ??
          });
        }

        await MeetingResolutionMW.UPDATE_MEETING_RESOLUTION_WITH_MEDIA(
          this.resolution,
          newMediaFiles,
          this.allChildrenResolutions
        )
          .then(() => {
            this.$emit("reloadTableData");
            this.isLoadingSave = false;
            this.cancel(true);
          })
          .catch(err => {
            this.isLoadingSave = false;
            // console.log("err", err);
          });

        if (this.isInMeetingStart && !this.meetingIsCompleted) {
          var self = this;
          self
            .$swal({
              info: "question",
              title: "Changements majeurs?",
              text: "",
              showConfirmButton: true,
              showCancelButton: true,
              confirmButtonText: "Oui",
              cancelButtonText: "No",
              // Configs ..
              reverseButtons: true,
              buttonsStyling: false,
              confirmButtonClass: "btn btn-primary btn-lg",
              cancelButtonClass: "btn btn-light btn-lg",
            })
            .then(async res => {
              if (res.value) {
                // NOTE: update votes sent by correspondence to nsp
                await self.updateVotesOnMajorChanges();
                console.log("YES");
              } else {
                self.updateVotesToRecentChangedMajority();
                console.log("NO");
              }
            })
            .catch(err => {
              console.log("err", err);
              self.$swal(self.alertError());
            });
        }
      } else {
        await ResolutionMW.UPDATE_RESOLUTION_WITH_MEDIA(
          this.resolution,
          this.allChildrenResolutions,
          newMediaFiles
        )
          .then(() => {
            this.isLoadingSave = false;
            this.cancel(true);
          })
          .catch(err => {
            this.isLoadingSave = false;
            console.log("err", err);
          });
      }
    },
    // !!!: ???: replicate files too?
    REPLICATE_RESOLUTION(resolution) {
      var replicateResolution = JSON.parse(JSON.stringify(resolution));
      replicateResolution.resolution_catalog_id = this.resolutionCatalogId;

      this.resolution = replicateResolution;
    },
    async updateVotesOnMajorChanges() {
      this.meetingCorrespondenceVotes.forEach(async correspondenceVotes => {
        let proposedVotes = correspondenceVotes.proposed_votes;
        if (proposedVotes && proposedVotes.length > 0) {
          let isVoteForMajorityExistent = false;
          proposedVotes.forEach(proposed_vote => {
            // NOTE: When is a major change, all votes with "FOR" will be set to "NSP" on correspondence
            if (
              proposed_vote.meeting_resolution_id == this.meetingResolutionId &&
              proposed_vote.vote == this.voteTypes.for
            ) {
              proposed_vote.vote = this.voteTypes.nsp;
            }
            // NOTE: Check if any votes for current majority exists, if not create a new vote for that below
            if (
              proposed_vote.meeting_resolution_id == this.meetingResolutionId &&
              proposed_vote.majority_id == this.meetingResolutionMajorityId
            ) {
              isVoteForMajorityExistent = true;
            }
          });

          if (!isVoteForMajorityExistent) {
            proposedVotes.push({
              majority_id: this.meetingResolutionMajorityId,
              meeting_resolution_id: this.meetingResolutionId,
              vote: this.voteTypes.nsp,
            });
          }

          MeetingCorrespondenceVoteORM.update({
            where: correspondenceVotes.id,
            data: {
              proposed_votes: proposedVotes,
            },
          });

          MeetingCorrespondenceVoteMW.PUT(correspondenceVotes);
        }
      });

      var votes = ResolutionVoteORM.query()
        .where("meeting_has_resolution_id", this.meetingResolutionId)
        .get();
      votes = this.$_.filter(votes, vote => {
        return vote.presence_status == this.mandatePresenceStatuses.correspondence;
      });
      if (votes.length == 0) {
        return;
      }
      votes.map(async vote => {
        if (vote.vote == this.voteTypes.for) {
          ResolutionVoteORM.update({
            where: vote.id,
            data: {
              vote: this.voteTypes.against,
            },
          });
        }
      });

      this.$emit("createResolutionVoteEvent", this.meetingId);
    },
    updateVotesToRecentChangedMajority() {
      var resolutionVotes = ResolutionVoteORM.query()
        .where("meeting_has_resolution_id", this.meetingResolutionId)
        .get();
      resolutionVotes = this.$_.filter(resolutionVotes, vote => {
        return vote.presence_status == this.mandatePresenceStatuses.correspondence;
      });

      resolutionVotes.forEach(resolutionVote => {
        this.meetingCorrespondenceVotes.forEach(async correspondenceVotes => {
          let proposedVotes = correspondenceVotes.proposed_votes;
          if (proposedVotes && proposedVotes.length > 0) {
            let isVoteForMajorityExistent = false;
            if (correspondenceVotes.mandate_id == resolutionVote.mandate_id) {
              // NOTE: Update proposed votes to current resolutionVote vote value
              proposedVotes.forEach(proposed_vote => {
                if (
                  proposed_vote.meeting_resolution_id == this.meetingResolutionId &&
                  proposed_vote.majority_id == this.meetingResolutionMajorityId
                ) {
                  proposed_vote.vote = resolutionVote.vote;
                  isVoteForMajorityExistent = true;
                }
              });

              // NOTE: Add new proposed vote for a majority id that was not voted before
              if (!isVoteForMajorityExistent) {
                proposedVotes.push({
                  majority_id: this.meetingResolutionMajorityId,
                  meeting_resolution_id: this.meetingResolutionId,
                  vote: resolutionVote.vote,
                });
              }
            }

            MeetingCorrespondenceVoteORM.update({
              where: correspondenceVotes.id,
              data: {
                proposed_votes: proposedVotes,
              },
            });

            MeetingCorrespondenceVoteMW.PUT(correspondenceVotes);
          }
        });
      });
    },
    removeFile(index, file) {
      MediaMW.DELETE(file.media)
        .then(() => {
          this.fileRecords.splice(index, 1);
        })
        .catch(err => {
          // console.log("err delete new media", err);
          this.$swal(this.alertError());
        });
    },
    DELETE_MEDIA(media) {
      MediaMW.DELETE(media).then(() => {
        // remove media from local resolution
        if (this.resolution.media) {
          this.resolution.media.splice(
            this.resolution.media.findIndex(item => item.id == media.id),
            1
          );
        }
      });
    },
    updateChildResolutionPosition(selectedPosition, object, index) {
      if (!object.position) return;
      object.position = Number(selectedPosition);
      // Extract element from array and put on another index, then update the others position value
      this.allChildrenResolutions.splice(index, 1);
      this.allChildrenResolutions.splice(selectedPosition - 1, 0, object);
      this.$_.each(this.allChildrenResolutions, (element, index2) => {
        if (object != element) {
          element.position = index2 + 1;
        }
      });
      this.keySubResolution = this.keySubResolution + 1;
    },
    addChildResolution() {
      if (this.allChildrenResolutions[0] && !this.allChildrenResolutions[0].position) {
        this.confirmChildResolution(this.allChildrenResolutions[0], 0);
      }
      let object = {
        parent_resolution_id: this.isInMeeting ? this.meetingResolutionId : this.resolutionId,
        resolution_catalog_id: this.resolutionCatalogId,
        agency_id: this.agencyId,
        name: "",
        description: "",
        majority_id: null,
        voting_category_id: null,
        keyId: uniqid(),
      };
      // Move to first position
      this.allChildrenResolutions.unshift(object);
      this.keySubResolution = this.keySubResolution + 1;
    },
    updateChildResolutionDescription(description, childResolution, index) {
      childResolution.description = description;
      this.updateChildResolution(childResolution, index);
    },
    updateChildResolution(childResolution, index) {
      this.allChildrenResolutions[index] = childResolution;
    },
    updateChildResolutionArrayPositions() {
      this.$_.each(this.allChildrenResolutions, (children, index) => {
        children.position = index + 1;
      });
      this.keySubResolution = this.keySubResolution + 1;
    },
    removeChildResolution(index) {
      this.allChildrenResolutions.splice(index, 1);
      this.removeChildResolutionNotConfirmed();
      this.updateChildResolutionArrayPositions();
    },
    confirmChildResolution(childResolution, index) {
      // Get last position from array
      let lastPosition = this.allChildrenResolutions.length;
      // Remove children resolution from index 0
      this.allChildrenResolutions.splice(index, 1);
      childResolution.position = lastPosition;
      // Add children resolution to last position
      this.allChildrenResolutions.push(childResolution);
      this.keySubResolution = this.keySubResolution + 1;
    },
    DELETE_CHILD_RESOLUTION(childResolution) {
      var self = this;
      self
        .$swal(self.alertQuestion())
        .then(res => {
          if (res.value) {
            var resolutionMW = self.isInMeeting ? MeetingResolutionMW : ResolutionMW;
            let resolutionORM = self.isInMeeting ? MeetingResolutionORM : ResolutionORM;

            resolutionMW
              .DELETE(childResolution)
              .then(() => {
                // Make sure to remove the children resolution from index 0 with position null
                this.removeChildResolutionNotConfirmed();
                // Remove from vuex
                resolutionORM.delete(childResolution.id);
                // Manually update resolution parent
                let parentId = this.isInMeeting ? this.meetingResolutionId : this.resolutionId;
                this.resolution = resolutionORM.find(parentId);
                // Remove from static children resolutions array
                let index = this.allChildrenResolutions.findIndex(
                  item => item.id == childResolution.id
                );
                this.allChildrenResolutions.splice(index, 1);
                this.updateChildResolutionArrayPositions();
              })
              .catch(err => {
                console.log("errdelete child resolution", err);
                self.$swal(this.alertError());
              });
          }
        })
        .catch(err => {
          console.log("err", err);
          self.$swal(self.alertError());
        });
    },
    changeResolutionNumber(e) {
      if (!this.isChildResolution) {
        e = e.toUpperCase();

        const split = this.resolution.resolution_number.split(".");
        this.resolution.resolution_number = split[0] + "." + e;
      } else {
        e = e.toLowerCase();
        this.resolution.resolution_number = e;
      }
    },
    updateFileRecords(fileRecords) {
      let isEqual = null;
      fileRecords.forEach(fileRecord => {
        let found = this.fileRecords.filter(file => file.media.id === fileRecord.media.id);
        if (found.length > 0) {
          isEqual = this.$_.isEqual(found[0].media, fileRecord.media);
        }

        if (found.length == 0 || !isEqual) {
          this.fileRecords.push(fileRecord);
        }
        this.updateChildrenResolutionMedia(fileRecord);
      });
    },
    deleteFileRecord(fileRecord) {
      this.fileRecords.splice(
        this.fileRecords.findIndex(item => item.media.id == fileRecord.media.id),
        1
      );
    },
    updateChildrenResolutionMedia(fileRecord) {
      let foundChResolutionIndex = this.allChildrenResolutions.findIndex(
        chRes =>
          (chRes.keyId && chRes.keyId == fileRecord.media.keyId) ||
          (chRes.id &&
            chRes.id == (fileRecord.media.resolution_id || fileRecord.meeting_has_resolution_id))
      );
      if (foundChResolutionIndex != "-1") {
        let foundChMedia = [];
        if (this.allChildrenResolutions[foundChResolutionIndex].media) {
          foundChMedia = this.allChildrenResolutions[foundChResolutionIndex].media.filter(
            file => file.id == fileRecord.media.id
          );
        }
        if (foundChMedia.length == 0) {
          if (this.allChildrenResolutions[foundChResolutionIndex].media) {
            this.allChildrenResolutions[foundChResolutionIndex].media.push(fileRecord.media);
          } else {
            this.allChildrenResolutions[foundChResolutionIndex].media = [fileRecord.media];
          }
        }
      }
    },
    removeChildResolutionNotConfirmed() {
      // Remove first childrenResolution without position
      if (this.allChildrenResolutions[0] && !this.allChildrenResolutions[0].position) {
        this.allChildrenResolutions.splice(0, 1);
      }
    },
    updateResolutionDescription(description) {
      this.resolution.description = description;
    },
    majoritiesOptions() {
      // NOTE: Add isInMeetingStart in order to prevent to enter when there is no meeting started
      if (!this.isInMeetingUpsert && this.isInMeetingStart) {
        let majorities = this.getMajoritiesWithSansVote();
        return this.$_.sortBy(majorities, m => m.name);
      }
      // NOTE: Return majorities related to building if buildingId exists
      const majorities = MajorityORM.query().orderBy("name").get();
      if (this.buildingId) {
        return this.filterMajoritiesByBuildingRelation(majorities);
      } else {
        return majorities;
      }
    },
    getMajoritiesWithSansVote() {
      // NOTE: If 'Sans Vote' is missing from MajoritiesData, we add it here
      let majorities = [...this.meetingDetails.MajoritiesData];
      if (!this.hasSansVoteInMajoritiesData()) {
        const sansVoteMajority = MajorityORM.query()
          .where("id", this.defaultSansVoteMajorityId)
          .first();
        if (sansVoteMajority) majorities.push(sansVoteMajority);
      }
      return majorities;
    },
    hasSansVoteInMajoritiesData() {
      if (this.meetingDetails && this.meetingDetails.MajoritiesData.length > 0) {
        return this.meetingDetails.MajoritiesData.find(
          item => item.id == this.defaultSansVoteMajorityId
        );
      } else return false;
    },
    filterMajoritiesByBuildingRelation(majorities) {
      let filtered = [];
      this.buildingMajorities.forEach(buildingMajority => {
        let majority = this.$_.find(majorities, item => item.id == buildingMajority.majority_id);
        if (majority) filtered.push(majority);
      });
      return filtered;
    },
    filterVotingCategories() {
      const meeting = MeetingORM.getters("getById")(this.meetingId);
      const meetingVotingCategories = meeting.voting_categories;

      const ormVotingCategories = VotingCategoryORM.query()
        .where("building_id", this.buildingId)
        .get();

      if (!this.isInMeeting || !meetingVotingCategories || meetingVotingCategories.length == 0) {
        return ormVotingCategories;
      }

      let filteredVotingCategories = [];
      for (const ormVotingCategory of ormVotingCategories) {
        const found = meetingVotingCategories.find(item => item == ormVotingCategory.id);
        if (found) {
          filteredVotingCategories.push(ormVotingCategory);
        }
      }
      return filteredVotingCategories;
    },
  },
};
</script>
